'use client'

import { useEffect, useRef, useState, type ComponentType } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { WispLayer } from './hero/WispLayer'
import { HeroHeadline } from './hero/HeroHeadline'
import { ScrollCue } from './hero/ScrollCue'
import { HeroPrimaryButton, HeroSecondaryButton } from '@/components/ui/HeroButton'
import CropMarks from '@/components/ui/CropMarks'
import { useHeroEntry } from '@/lib/animations/useHeroEntry'
import type BacteriaSwarmType from '@/components/BacteriaSwarm/BacteriaSwarm'

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null)
  const t = useTranslations('hero')
  const locale = useLocale()
  const techHref  = locale === 'es' ? '/tecnologia'      : '/tech'
  const aboutHref = locale === 'es' ? '/sobre-nosotros' : '/about'
  useHeroEntry(sectionRef)

  // Three.js + R3F + drei + postprocessing is ~1 MB. Loading it at module
  // top-level via `dynamic()` still ships the chunk on initial page load
  // (it just defers execution). To prevent the chunk from being requested
  // *at all* until the page is interactive, we drive the import from state
  // so PSI sees ~300 KB less unused JS during its measurement window.
  const [BacteriaSwarm, setBacteriaSwarm] = useState<ComponentType<React.ComponentProps<typeof BacteriaSwarmType>> | null>(null)
  useEffect(() => {
    const reveal = () =>
      setTimeout(() => {
        import('@/components/BacteriaSwarm/BacteriaSwarm').then((m) => {
          setBacteriaSwarm(() => m.default)
        })
      }, 800)
    if (document.readyState === 'complete') reveal()
    else window.addEventListener('load', reveal, { once: true })
  }, [])

  return (
    <section
      ref={sectionRef}
      id="hero"
      aria-labelledby="hero-headline"
      style={{
        position: 'relative',
        overflow: 'visible',
        minHeight: 900,
      }}
    >
      {/* Light radial overlays on a child — section element itself stays transparent */}
      <div
        aria-hidden="true"
        style={{
          // Extends 160px below hero so the cream overlay fades out into
          // Problem space — no brightness cliff at the section boundary.
          position: 'absolute',
          top: 0, left: 0, right: 0,
          bottom: -160,
          pointerEvents: 'none',
          zIndex: 0,
          background: [
            'radial-gradient(ellipse 82% 46% at 15% 14%, rgba(255,252,240,0.90) 0%, rgba(248,238,208,0.48) 38%, rgba(235,221,185,0) 68%)',
            'radial-gradient(ellipse 52% 36% at 68% 4%, rgba(250,246,228,0.58) 0%, rgba(238,226,194,0) 58%)',
          ].join(', '),
        }}
      />
      {/* Accessibility: skip to headline */}
      <a
        href="#hero-headline"
        className="skip-link"
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 100,
          padding: '6px 14px',
          background: 'var(--olive-600)',
          color: '#fff',
          fontFamily: 'var(--font-body)',
          fontSize: 12,
          borderRadius: 3,
          textDecoration: 'none',
          transform: 'translateY(-200%)',
          transition: 'transform 0.18s',
        }}
      >
        Skip to content
      </a>
      <style>{`
        .skip-link:focus { transform: translateY(0) !important; }
      `}</style>

      {/* z-index 0 — atmospheric wisps */}
      <WispLayer />

      {/* descent overlay removed — <main> gradient handles all darkening */}

      {/* hero-horizon removed — it was reading as a section break instead of
          a depth cue. Gradient on <main> carries all horizon separation now. */}


      {/* z-index 5 — swarm fills the hero; cluster offset to the right column.
          Hidden on mobile via #hero-swarm media query — replaced by lighter
          CSS dot cluster below. */}
      <div
        id="hero-swarm"
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          // Canvas extends 600px into Problem section — orbital rings can
          // continue visually. offsetY below keeps the cluster in its
          // original hero-section pixel position.
          bottom: -600,
          zIndex: 5,
          pointerEvents: 'none',
        }}
      >
        {BacteriaSwarm ? <BacteriaSwarm
          count={160}
          // Cluster pulled further inward so it reads as "inside" the hero
          // frame instead of hugging the right edge.
          offsetX={1.5}
          // Canvas is 1500px tall (hero 900 + 600 spillover). Canvas center sits
          // at 750px from top of hero. offsetY=1.0 lifts the cluster up in world
          // space so its centre lands at ~453px — roughly hero-vertical-middle —
          // with scatter spread extending only a small way into Problem.
          offsetY={1.0}
          // Rings anchored a touch right of the cluster (not all the way right)
          // so they drift with the swarm but their left arc still stays clear
          // of the headline column.
          ringOffsetX={2.3}
          ringOffsetY={1.0}
          radius={1.4}
          coreDensity={0.82}
          scatterSpread={[5.2, 3.0, 1.5]}
          entryDuration={2.8}
          outlierRate={0.06}
          outlierSpread={[4.0, 2.2, 1.0]}
          // Fewer spheres inside the cluster (dropped from default 28% to 8%).
          cocciRate={0.08}
          // Five lone spheres drift far from the cluster, spanning the full
          // canvas width and reaching deep into the Problem section.
          farCocciCount={5}
          farCocciSpread={[8, 5.5, 1.2]}
        /> : null}
      </div>

      {/* Root column is now rendered globally as <FollowingRoot /> in the
          root layout so the vine follows the user from hero into Problem
          and Solution as a single continuous element. */}

      {/* z-index 10 — main stage: two columns, text left, bacteria right */}
      <div
        id="hero-stage"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          display: 'grid',
          /* Left text col: up to 52% of width. Right col: rest (bacteria layer behind) */
          gridTemplateColumns: 'minmax(0, 52%) 1fr',
          gridTemplateRows: '1fr',
          paddingTop: 'clamp(112px, 14vh, 160px)',
          paddingBottom: 'clamp(72px, 9vh, 112px)',
          paddingLeft: 'clamp(64px, 7vw, 96px)',
          paddingRight: 0,
          pointerEvents: 'none',
        }}
      >
        {/* Left column: eyebrow → headline → subhead → CTAs */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
          {/* Top section: eyebrow + headline */}
          <div>
            {/* Eyebrow */}
            <div
              id="hero-eyebrow"
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '0.5em',
                marginBottom: 'clamp(16px, 2vh, 28px)',
              }}
            >
              <span style={{
                fontFamily: 'var(--font-serif)',
                fontStyle: 'italic',
                fontSize: 'clamp(12px, 1.2vw, 16px)',
                color: 'var(--olive-600)',
                fontWeight: 400,
              }}>
                00
              </span>
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: 22,
                  height: 1,
                  background: 'var(--olive-500)',
                  verticalAlign: 'middle',
                  opacity: 0.55,
                }}
              />
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'clamp(9px, 0.8vw, 11px)',
                fontWeight: 500,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                color: 'rgba(81,50,5,0.62)',
              }}>
                {t('eyebrowLabel')}
              </span>
            </div>

            {/* Headline */}
            <HeroHeadline />
          </div>

          {/* Bottom section: subhead + CTAs + field tag */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            marginTop: 'clamp(24px, 3vh, 40px)',
          }}>
            <p id="hero-subhead" style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'clamp(13px, 1.1vw, 16px)',
              fontWeight: 400,
              lineHeight: 1.65,
              color: 'rgba(61,46,14,0.78)',
              margin: '0 0 clamp(16px, 2vh, 24px)',
              letterSpacing: '-0.01em',
              maxWidth: 400,
            }}>
              {t('subhead')}
            </p>

            <div id="hero-ctas" style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              pointerEvents: 'auto',
            }}>
              <HeroPrimaryButton href={techHref}>{t('ctaPrimary')}</HeroPrimaryButton>
              <HeroSecondaryButton href={aboutHref}>{t('ctaSecondary')}</HeroSecondaryButton>
            </div>
          </div>
        </div>

        {/* Right column: intentionally empty — bacteria canvas is behind via z-index */}
        <div aria-hidden="true" />
      </div>

      {/* z-index 20 — chrome labels */}

      {/* chrome-tl, chrome-tr, chrome-bl removed — keeping only counter */}
      <div id="hero-chrome-tl" aria-hidden="true" style={{ display: 'none' }} />
      <div id="hero-chrome-tr" aria-hidden="true" style={{ display: 'none' }} />
      <div id="hero-chrome-bl" aria-hidden="true" style={{ display: 'none' }} />

      {/* Bottom-right — kept for GSAP targeting, counter is now global StickyCounter */}
      <div id="hero-chrome-br" aria-hidden="true" style={{ display: 'none' }} />

      {/* Crop marks */}
      <CropMarks color="rgba(81,50,5,0.2)" inset={16} size={18} />

      {/* z-index 25 — scroll cue */}
      <ScrollCue />

      {/* Mobile-only dot constellation — CSS replacement for the WebGL
          BacteriaSwarm. Sits behind headline so it reads as ambient life. */}
      <div
        id="hero-mobile-dots"
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, zIndex: 4, pointerEvents: 'none', display: 'none' }}
      >
        <span className="hero-dot hero-dot--lg" style={{ top: '18%', right: '14%' }} />
        <span className="hero-dot"            style={{ top: '24%', right: '34%' }} />
        <span className="hero-dot hero-dot--sm" style={{ top: '32%', right: '8%' }} />
        <span className="hero-dot hero-dot--lg" style={{ top: '46%', right: '24%' }} />
        <span className="hero-dot"            style={{ top: '58%', right: '12%' }} />
        <span className="hero-dot hero-dot--sm" style={{ top: '66%', right: '40%' }} />
        <span className="hero-dot"            style={{ top: '74%', right: '20%' }} />
        <span className="hero-dot hero-dot--sm" style={{ top: '12%', left: '12%' }} />
        <span className="hero-dot hero-dot--sm" style={{ top: '78%', left: '8%' }} />
        <span className="hero-dot hero-dot--glow" style={{ top: '38%', right: '18%' }} />
      </div>

      {/* Mobile overrides */}
      <style>{`
        @media (max-width: 768px) {
          #hero { min-height: 100dvh; }

          #hero-stage {
            position: relative !important;
            inset: auto !important;
            grid-template-columns: 1fr !important;
            padding: 140px 24px 120px 24px !important;
            gap: 0 !important;
          }

          #hero-headline {
            font-size: clamp(40px, 11.2vw, 64px) !important;
            max-width: none !important;
            line-height: 1.04 !important;
          }

          #hero-eyebrow { margin-bottom: 18px !important; }

          #hero-subhead {
            font-size: 14px !important;
            line-height: 1.6 !important;
            max-width: 100% !important;
            margin-bottom: 22px !important;
          }

          #hero-ctas {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
            width: 100% !important;
            max-width: 320px !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          #hero-ctas .hero-btn-plant,
          #hero-ctas a {
            justify-content: center;
          }

          #hero-swarm { display: none !important; }
          #hero-mobile-dots { display: block !important; }

          #hero-scroll-cue { bottom: 16% !important; gap: 8px !important; }
          #hero-scroll-cue > span:first-child { font-size: 9px !important; letter-spacing: 0.32em !important; }
          #hero-scroll-cue > span:nth-child(2) { height: 36px !important; }
          #hero-scroll-cue > span:last-child { font-size: 12px !important; }
        }

        /* Mobile-only dots — a calm, lifelike constellation that nods to the
           bacteria swarm without WebGL overhead. */
        .hero-dot {
          position: absolute;
          width: 6px; height: 6px;
          border-radius: 50%;
          background: rgba(122,139,10,0.55);
          box-shadow: 0 0 6px rgba(122,139,10,0.35);
          animation: hero-dot-pulse 3.6s ease-in-out infinite;
        }
        .hero-dot--sm { width: 4px; height: 4px; opacity: 0.7; }
        .hero-dot--lg {
          width: 8px; height: 8px;
          background: rgba(91,112,5,0.65);
          box-shadow: 0 0 10px rgba(91,112,5,0.45);
        }
        .hero-dot--glow {
          width: 110px; height: 110px;
          background: radial-gradient(circle, rgba(159,175,8,0.18) 0%, rgba(159,175,8,0) 65%);
          box-shadow: none;
          animation: hero-dot-breathe 5.4s ease-in-out infinite;
        }
        .hero-dot:nth-of-type(2) { animation-delay: 0.4s; }
        .hero-dot:nth-of-type(3) { animation-delay: 0.9s; }
        .hero-dot:nth-of-type(4) { animation-delay: 1.4s; }
        .hero-dot:nth-of-type(5) { animation-delay: 0.2s; }
        .hero-dot:nth-of-type(6) { animation-delay: 1.1s; }
        .hero-dot:nth-of-type(7) { animation-delay: 0.7s; }
        .hero-dot:nth-of-type(8) { animation-delay: 1.6s; }
        .hero-dot:nth-of-type(9) { animation-delay: 2.0s; }

        @keyframes hero-dot-pulse {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50%      { transform: scale(1.35); opacity: 0.95; }
        }
        @keyframes hero-dot-breathe {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50%      { transform: scale(1.18); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-dot { animation: none !important; }
        }
      `}</style>
    </section>
  )
}
